/**
 * MediaSFU brand surfaces for the private meeting experience.
 *
 * The shared `MediasfuColors` defaults already carry the MediaSFU accent
 * (teal and sky, deep enough for the white text the SDK draws on them). The
 * private, limited build used on mediasfu.com goes one step further and puts
 * the modern components on the design system's ink-navy surfaces and text
 * tones.
 *
 * This module assigns those surface tokens onto the shared static fields once,
 * before the limited components first render. TypeScript's `readonly` is
 * compile-time only and the class is not frozen, so these are ordinary property
 * writes. No component caches a colour at module scope, so every render after
 * this call sees them. Only bundles that import `components_limited` run it.
 */
import { MediasfuColors } from "../components_modern/core/theme/MediasfuColors";

/** Design-system tokens, for limited components that style themselves. */
export const MEDIASFU_BRAND = {
  teal: "#2DD4BF",
  tealStrong: "#14B8A6",
  tealLight: "#5EEAD4",
  /** Teal that keeps contrast as text on white. */
  tealText: "#0D9488",
  sky: "#38BDF8",
  skyLight: "#7DD3FC",
  /** Ink text on the teal accent or the brand gradient. Never white. */
  ink: "#03151A",
  gradient: "linear-gradient(135deg, #2DD4BF 0%, #38BDF8 100%)",
  dark: {
    page: "#060A13",
    surface: "#0B1220",
    surfaceAlt: "#0C1424",
    raised: "#111B2E",
    popover: "#0D1626",
    line: "rgba(148, 163, 184, 0.12)",
    lineStrong: "rgba(148, 163, 184, 0.22)",
    text: "#E8EEF6",
    soft: "#C3CFDD",
    muted: "#8394AA",
    fill: "rgba(255, 255, 255, 0.035)",
    fillHover: "rgba(255, 255, 255, 0.07)",
  },
  light: {
    page: "#F3F6FA",
    surface: "#FFFFFF",
    surfaceAlt: "#F8FAFC",
    raised: "#EEF2F7",
    popover: "#FFFFFF",
    line: "rgba(15, 23, 42, 0.08)",
    lineStrong: "rgba(15, 23, 42, 0.14)",
    text: "#0B1220",
    soft: "#475569",
    muted: "#64748B",
    fill: "rgba(15, 23, 42, 0.03)",
    fillHover: "rgba(15, 23, 42, 0.06)",
  },
  success: "#34D399",
  warning: "#FBBF24",
  danger: "#FB7185",
} as const;

/** Picks the surface/text roles for the current mode. */
export const brandSurface = (isDarkMode: boolean) =>
  isDarkMode ? MEDIASFU_BRAND.dark : MEDIASFU_BRAND.light;

let applied = false;

/**
 * Applies the MediaSFU palette to the modern component tokens. Idempotent;
 * safe to call from several modules.
 */
export function applyMediasfuBrandPalette(): void {
  if (applied) return;
  applied = true;

  const B = MEDIASFU_BRAND;
  const tokens: Record<string, string> = {
    // Light surfaces: cool paper, hairline dividers.
    surface: B.light.surface,
    surfaceElevated: "#F3F6FA",
    background: "#F6F8FB",
    card: B.light.surface,
    cardHover: "#F4F7FB",
    divider: "#E3E8EF",

    // Dark surfaces: deep ink-navy.
    surfaceDark: B.dark.surface,
    surfaceElevatedDark: B.dark.raised,
    backgroundDark: B.dark.page,
    cardDark: B.dark.surfaceAlt,
    cardHoverDark: B.dark.raised,
    dividerDark: "#1C2638",

    // Semantic, aligned to the design system.
    success: B.success,
    warning: B.warning,
    danger: B.danger,
    info: B.sky,

    // Text.
    textPrimary: B.light.text,
    textSecondary: B.light.soft,
    textMuted: B.light.muted,
    textPrimaryDark: B.dark.text,
    textSecondaryDark: B.dark.soft,
    textMutedDark: B.dark.muted,
  };

  const target = MediasfuColors as unknown as Record<string, unknown>;
  for (const [key, value] of Object.entries(tokens)) {
    target[key] = value;
  }
}
